import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { safeNext, withNext } from "@/lib/next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import Header from "@/components/Header";
import { ArrowRight } from "lucide-react";

export default function Register() {
  const { register, formatApiError } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (!agreed) {
      toast.error("Please accept the Terms & Conditions and Privacy Policy");
      return;
    }
    setLoading(true);
    try {
      await register(name, email, password, agreed);
      toast.success("Account created");
      navigate(safeNext());
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="register-page">
      <Header />
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-5">
            <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#52525B] mb-3">Create account</div>
            <h1 className="text-4xl md:text-5xl tracking-tighter font-black mb-6">Start the<br />conversion.</h1>
            <p className="text-[#52525B] text-base leading-relaxed max-w-md">
              Free to start. No credit card. Your vectorized assets are stored
              privately in your workspace.
            </p>
          </div>
          <div className="md:col-span-7 md:col-start-7">
            <form
              onSubmit={submit}
              className="border border-black/10 bg-[#F8F9FA] p-8 md:p-12 max-w-md ml-auto"
              data-testid="register-form"
            >
              <div className="space-y-6">
                <div>
                  <Label htmlFor="name" className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#52525B]">Name</Label>
                  <Input
                    id="name"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    data-testid="register-name-input"
                    className="mt-2 rounded-none bg-white border-black/15 focus-visible:ring-2 focus-visible:ring-[#ED5A24] focus-visible:ring-offset-0 h-11"
                    placeholder="Jane Studio"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#52525B]">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="register-email-input"
                    className="mt-2 rounded-none bg-white border-black/15 focus-visible:ring-2 focus-visible:ring-[#ED5A24] focus-visible:ring-offset-0 h-11"
                    placeholder="you@studio.com"
                  />
                </div>
                <div>
                  <Label htmlFor="password" className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#52525B]">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    required
                    minLength={6}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="register-password-input"
                    className="mt-2 rounded-none bg-white border-black/15 focus-visible:ring-2 focus-visible:ring-[#ED5A24] focus-visible:ring-offset-0 h-11"
                    placeholder="Minimum 6 characters"
                  />
                </div>
                <label className="flex items-start gap-3 text-sm text-[#52525B] cursor-pointer" data-testid="register-terms-label">
                  <input
                    type="checkbox"
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    data-testid="register-terms-checkbox"
                    className="mt-0.5 w-4 h-4 accent-[#ED5A24] cursor-pointer"
                  />
                  <span>
                    I agree to the{" "}
                    <Link to="/terms" target="_blank" className="text-[#ED5A24] underline underline-offset-4" data-testid="register-terms-link">Terms & Conditions</Link>
                    {" "}and{" "}
                    <Link to="/privacy" target="_blank" className="text-[#ED5A24] underline underline-offset-4" data-testid="register-privacy-link">Privacy Policy</Link>.
                  </span>
                </label>
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="register-submit-btn"
                  className="w-full rounded-none bg-[#ED5A24] hover:bg-[#C44619] text-white text-xs uppercase tracking-[0.2em] font-bold h-12"
                >
                  {loading ? "Creating…" : "Create account"} <ArrowRight className="w-4 h-4" />
                </Button>
                <div className="text-sm text-[#52525B] text-center">
                  Already have one?{" "}
                  <Link to={withNext("/login")} data-testid="goto-login" className="text-[#ED5A24] underline underline-offset-4 hover:text-[#C44619]">
                    Sign in
                  </Link>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
